// Copyright ©️ 2026 eVolve MEP, LLC

import { Fragment, useState } from 'react';

import type { Task, TaskId, WorkCellTask } from '@evolvemep/api-ts';
import { Flex, Stack, Text, Title } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { RunningTime } from 'components/Mantine/RunningTime';
import { isNotNil } from 'helpers/isNotNil';
import { ManageTaskTimeControls } from 'modules/Shop/Tasks/ManageTaskTimeModal/ManageTaskTimeControls';
import { TaskTimeTaskLoader } from 'modules/Shop/Tasks/ManageTaskTimeModal/TaskTimeTaskLoader';

type Props = {
  taskId: TaskId;
  onTaskUpdated: (t: Task) => void;
  workCellTask?: WorkCellTask;
  opened: boolean;
  onClose: () => void;
};

export const ManageTaskTimeModal = ({ taskId, workCellTask, onTaskUpdated, opened, onClose }: Props) => {
  const [task, setTask] = useState<Task | null>(null);

  return (
    <WrappedModal
      miniHeader={
        <Flex align="center" gap={4} wrap="wrap">
          {[workCellTask?.workOrderIdentifier, workCellTask?.workOrderName, task?.taskName]
            .filter(isNotNil)
            .map((part, index) => (
              // oxlint-disable-next-line react/no-array-index-key
              <Fragment key={index}>
                {index > 0 && <EvolveIcon icon="ArrowRight" size="xs" />}
                <Text size="sm" c="dimmed">
                  {part}
                </Text>
              </Fragment>
            ))}
        </Flex>
      }
      title={
        <Stack flex={1} mih={90} align="center" gap={0}>
          <Text c="dimmed" size="xs">
            Timer
          </Text>
          <Title order={1} size={36} style={{ alignSelf: 'center' }}>
            {isNotNil(task) ? (
              <RunningTime
                timeInSeconds={task.time.totalTime}
                isRunning={task.taskStatusTypeName === 'Started'}
                showSeconds
              />
            ) : (
              '--:--:--'
            )}
          </Title>
        </Stack>
      }
      opened={opened}
      onConfirm={onClose}
      onClose={onClose}
      bodyProps={{ p: 0 }}
      hideModalButtons
    >
      <TaskTimeTaskLoader taskId={taskId} onTaskLoaded={setTask} task={task}>
        {(loadedTask) => (
          <ManageTaskTimeControls
            task={loadedTask}
            workCellTask={workCellTask}
            onTaskUpdated={(t) => {
              onTaskUpdated(t);
              setTask(t);
            }}
            onClose={onClose}
          />
        )}
      </TaskTimeTaskLoader>
    </WrappedModal>
  );
};
